/**
 * Finanzen – sensibler Bereich.
 *
 * Vier Kennzahlen, Monatsverlauf, kumulierte Jahresansicht, Gewinnprognose und
 * die Ablage der Buchhaltungsberichte.
 *
 * Zur Beschriftung: alle Beträge sind ohne Umsatzsteuer. Der Gewinn ist aber
 * NICHT „vor Steuern" – er stammt aus der Saldenliste und enthält deren
 * Kontenklasse 8, also die bereits gebuchte Körperschaftsteuer-Vorauszahlung.
 * Der Prototyp schrieb an mehreren Stellen „vor Steuern"; das war für den
 * Auftragswert richtig (dort heißt es: ohne USt) und für den Gewinn falsch.
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { IconSchloss } from '../komponenten/Icons';
import { betragLesen, gruppiere } from '../hilfen/format';
import type {
  BerichtAbgleich,
  BerichteAntwort,
  Finanzen as FinanzenDaten,
  SaldenAbgleich,
} from '../typen';
import './finanzen.css';

export function Finanzen() {
  const { toastZeigen } = useKora();
  const meta = useMeta();
  const queryClient = useQueryClient();
  const dateiFeld = useRef<HTMLInputElement>(null);
  const [monat, setMonat] = useState('');
  const [uploadFehler, setUploadFehler] = useState('');
  const [monateOffen, setMonateOffen] = useState(false);

  /**
   * Auswertungszeitraum. Leer heißt „wie der Server voreinstellt" – das ist
   * das Jahr des jüngsten Berichts, begrenzt auf die Monate, die es gibt.
   */
  const [zeitraum, setZeitraum] = useState<{ von: string; bis: string } | null>(null);

  const finanzen = useQuery({
    queryKey: ['finanzen', zeitraum?.von ?? '', zeitraum?.bis ?? ''],
    queryFn: () =>
      api.hole<FinanzenDaten>(
        zeitraum ? `/finanzen?von=${zeitraum.von}&bis=${zeitraum.bis}` : '/finanzen',
      ),
    // Beim Umschalten die alten Zahlen stehen lassen, statt die Seite leer
    // werden zu lassen – sonst springt das Layout bei jedem Klick.
    placeholderData: (vorher) => vorher,
  });

  const berichte = useQuery({
    queryKey: ['berichte'],
    queryFn: () => api.hole<BerichteAntwort>('/finanzen/berichte'),
  });

  const hochladen = useMutation({
    mutationFn: (datei: File) => {
      const formular = new FormData();
      formular.append('monat', monat || berichte.data?.monate[0]?.wert || '');
      formular.append('datei', datei);
      return api.datei<{
        id: number;
        ersetzt: boolean;
        pfad: string | null;
        hinweisSharePoint: boolean;
        fehler: string | null;
      }>('/finanzen/berichte', formular);
    },
    onSuccess: (antwort) => {
      void queryClient.invalidateQueries({ queryKey: ['berichte'] });
      if (antwort.hinweisSharePoint) {
        setUploadFehler(
          antwort.fehler
            ? `In KORA vermerkt, aber die Ablage in SharePoint hat nicht geklappt: ${antwort.fehler}`
            : 'In KORA vermerkt, aber die Ablage in SharePoint hat nicht geklappt – siehe Protokoll.',
        );
        return;
      }
      toastZeigen(
        antwort.ersetzt
          ? 'Bericht ersetzt – die vorherige Fassung dieses Monats wurde überschrieben.'
          : 'Buchhaltungsbericht abgelegt.',
      );
    },
    onError: (f) => setUploadFehler(f instanceof Error ? f.message : 'Hochladen fehlgeschlagen.'),
  });

  /**
   * Erst abgleichen, dann übernehmen. Der Abgleich schreibt nichts – er zeigt
   * nur, was passieren würde. Ein Knopf, der ungefragt Datensätze anlegt, ist
   * unangenehm.
   */
  const [abgleich, setAbgleich] = useState<BerichtAbgleich | null>(null);

  const pruefen = useMutation({
    mutationFn: () => api.hole<BerichtAbgleich>('/finanzen/berichte/abgleich'),
    onSuccess: (a) => {
      setUploadFehler('');
      setAbgleich(a);
    },
    onError: (f) => setUploadFehler(f instanceof Error ? f.message : 'Abgleich fehlgeschlagen.'),
  });

  /** Berichte übernehmen, die in SharePoint schon liegen. */
  const einlesen = useMutation({
    mutationFn: () =>
      api.sende<{ gefunden: number; neu: number; ergaenzt: number; jahre: string[] }>(
        '/finanzen/berichte/einlesen',
      ),
    onSuccess: (a) => {
      void queryClient.invalidateQueries({ queryKey: ['berichte'] });
      setUploadFehler('');
      setAbgleich(null);
      toastZeigen(
        a.neu || a.ergaenzt
          ? `${a.gefunden} Berichte gefunden · ${a.neu} neu, ${a.ergaenzt} verknüpft.`
          : `${a.gefunden} Berichte gefunden – alle waren schon da.`,
      );
    },
    onError: (f) => setUploadFehler(f instanceof Error ? f.message : 'Einlesen fehlgeschlagen.'),
  });

  const monatSpeichern = useMutation({
    mutationFn: ({
      monat: m,
      umsatz,
      ausgaben,
      stand,
      quelle,
    }: {
      monat: string;
      umsatz: number;
      ausgaben: number;
      stand?: string;
      quelle?: string;
    }) => api.ersetze(`/finanzen/monat/${m}`, { umsatz, ausgaben, stand, quelle }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['finanzen'] }),
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  /**
   * Zahlen aus den Saldenlisten. Auch hier zwei Schritte: erst zeigen, was
   * herauskommt, dann übernehmen. Bei Geldbeträgen erst recht.
   */
  const [salden, setSalden] = useState<SaldenAbgleich | null>(null);

  const saldenPruefen = useMutation({
    mutationFn: () => api.hole<SaldenAbgleich>('/finanzen/salden/abgleich'),
    onSuccess: (a) => {
      setUploadFehler('');
      setSalden(a);
    },
    onError: (f) =>
      setUploadFehler(f instanceof Error ? f.message : 'Die Saldenlisten waren nicht lesbar.'),
  });

  const saldenUebernehmen = useMutation({
    mutationFn: () =>
      api.sende<{ uebernommen: number; uebersprungen: number }>('/finanzen/salden/uebernehmen'),
    onSuccess: (a) => {
      void queryClient.invalidateQueries({ queryKey: ['finanzen'] });
      setSalden(null);
      toastZeigen(
        `${a.uebernommen} Monate übernommen` +
          (a.uebersprungen ? `, ${a.uebersprungen} ohne auswertbare Saldenliste.` : '.'),
      );
    },
    onError: (f) => setUploadFehler(f instanceof Error ? f.message : 'Übernehmen fehlgeschlagen.'),
  });

  const notiz = useMutation({
    mutationFn: ({ id, text }: { id: number; text: string }) =>
      api.aendere(`/finanzen/berichte/${id}`, { notiz: text }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['berichte'] }),
  });

  const d = finanzen.data;
  const b = berichte.data;

  /** Zielpfad des gewählten Monats – vom Server gebildet, nicht hier. */
  const gewaehlterMonat = monat || b?.monate[0]?.wert || '';
  const zielPfad = b?.monate.find((m) => m.wert === gewaehlterMonat)?.pfad ?? '';

  return (
    <div className="finanzen">
      <div className="eh-kopfzeile">
        <div>
          <div className="eh-kicker">Kaufmännisches</div>
          <div className="eh-titel">Finanzen</div>
          <div className="fin-untertitel">
            Buchhaltungsberichte, Ist-Zahlen und Gewinnprognose – alle Beträge ohne Umsatzsteuer.
          </div>
        </div>
        <div className="fin-zugriff">
          <span className="fin-zugriff-icon">
            <IconSchloss />
          </span>
          <span>
            Zugriff: Geschäftsführung und Kaufmännisches. Wer diesen Bereich sieht, legt die
            Administration im Admin Center fest.
          </span>
        </div>
      </div>

      {d && (
        <Zeitraumwahl
          daten={d}
          laeuft={finanzen.isFetching}
          onWaehlen={(von, bis) => setZeitraum({ von, bis })}
        />
      )}

      {finanzen.isLoading && <div className="eh-karte">Zahlen werden geladen …</div>}

      {d && (
        <>
          <div className="fin-kpis">
            {d.kpis.map((k) => (
              <div className="fin-kpi" key={k.label}>
                <span className="fin-kpi-label">{k.label}</span>
                <span className="fin-kpi-wert" style={{ color: k.farbe }}>
                  {k.wert}
                </span>
                <span className="fin-kpi-hinweis">{k.hinweis}</span>
              </div>
            ))}
          </div>

          {/* Der jüngste erfasste Monat beantwortet „wie steht es gerade?" –
              und nennt dazu, worauf sich die Zahlen stützen. */}
          {d.letzterMonat && (
            <div className="fin-karte fin-letzter">
              <div className="fin-karte-kopf">
                <div className="fin-label">Letzter Buchhaltungsbericht · {d.letzterMonat.label}</div>
                <div className="fin-anzahl">
                  {d.letzterMonat.standText
                    ? `Stand ${d.letzterMonat.standText}${d.letzterMonat.quelle ? ` · ${d.letzterMonat.quelle}` : ''}`
                    : 'Kein Stand hinterlegt – bitte unter „Zahlen pflegen" nachtragen'}
                </div>
              </div>
              <div className="fin-letzter-werte">
                <div className="fin-letzter-wert">
                  <span className="fin-kpi-label">Umsatz</span>
                  <span className="fin-letzter-zahl">{d.letzterMonat.umsatz}</span>
                </div>
                <div className="fin-letzter-wert">
                  <span className="fin-kpi-label">Ausgaben</span>
                  <span className="fin-letzter-zahl">{d.letzterMonat.ausgaben}</span>
                </div>
                <div className="fin-letzter-wert">
                  <span className="fin-kpi-label">Gewinn laut Buchhaltung</span>
                  <span className="fin-letzter-zahl" style={{ color: 'var(--eh-rot)' }}>
                    {d.letzterMonat.gewinn}
                  </span>
                </div>
                <div className="fin-letzter-wert">
                  <span className="fin-kpi-label">Marge</span>
                  <span className="fin-letzter-zahl">
                    {d.letzterMonat.marge === null ? '—' : `${d.letzterMonat.marge} %`}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="fin-karte">
            <div className="fin-karte-kopf">
              <div className="fin-label">Monatsverlauf {d.zeitraum.label} · ohne Umsatzsteuer</div>
              <div className="fin-legende">
                <span>
                  <span className="fin-legende-punkt" style={{ background: '#2A2320' }} />
                  Umsatz
                </span>
                <span>
                  <span className="fin-legende-punkt" style={{ background: '#CBBCAC' }} />
                  Ausgaben
                </span>
                <span>
                  <span className="fin-legende-punkt" style={{ background: 'var(--eh-rot)' }} />
                  Gewinn
                </span>
                <button
                  type="button"
                  className="fin-pflege-knopf"
                  onClick={() => setMonateOffen((o) => !o)}
                >
                  {monateOffen ? 'Zahlen ausblenden' : 'Zahlen pflegen'}
                </button>
                {b?.buchhaltungAktiv && !salden && (
                  <button
                    type="button"
                    className="fin-einlesen"
                    onClick={() => saldenPruefen.mutate()}
                    disabled={saldenPruefen.isPending}
                    title="Liest die Saldenlisten aus SharePoint und rechnet gegen deren eigene Ergebniszeile"
                  >
                    {saldenPruefen.isPending
                      ? 'Saldenlisten werden gelesen …'
                      : 'Zahlen aus Saldenlisten holen'}
                  </button>
                )}
              </div>
            </div>

            {salden && (
              <div className="fin-abgleich">
                <div className="fin-abgleich-kopf">
                  Jeder Monat wurde gegen die Ergebniszeile seiner eigenen Saldenliste gerechnet.
                  Übernommen wird nur, was übereinstimmt.
                </div>

                <div className="fin-salden-tabelle">
                  <div className="fin-salden-zeile fin-salden-kopf">
                    <div>Monat</div>
                    <div>Umsatz</div>
                    <div>Ausgaben</div>
                    <div>Gewinn</div>
                    <div>Stand</div>
                    <div />
                  </div>
                  {salden.monate.map((m) => (
                    <div className="fin-salden-zeile" key={m.monat}>
                      <div className="fin-salden-monat">{m.label}</div>
                      {m.lesbar ? (
                        <>
                          <div className="fin-salden-zahl">{m.umsatzText}</div>
                          <div className="fin-salden-zahl">{m.ausgabenText}</div>
                          <div
                            className="fin-salden-zahl"
                            style={{
                              color: (m.gewinn ?? 0) < 0 ? 'var(--eh-rot)' : 'var(--eh-gruen)',
                            }}
                          >
                            {m.gewinnText}
                          </div>
                          <div className="fin-salden-stand">{m.stand ?? '—'}</div>
                          <div className={`fin-salden-marke ${m.aenderung}`}>{m.aenderung}</div>
                        </>
                      ) : (
                        <div className="fin-salden-hinweis">{m.hinweis}</div>
                      )}
                    </div>
                  ))}
                </div>

                {salden.jahre
                  .filter((j) => j.monate > 0)
                  .map((j) => (
                    <div className="fin-salden-jahr" key={j.jahr}>
                      <strong>{j.jahr}</strong>: Summe der {j.monate} Monate {j.summeMonate}, laut
                      jüngster Liste {j.kumuliert} seit Jahresbeginn.
                      {Math.abs(j.abweichendeCent) > 100 && (
                        <>
                          {' '}
                          Der Unterschied von {j.abweichung} kommt von Nachbuchungen in bereits
                          gedruckten Monaten – die kumulierte Zahl ist die aktuellere.
                        </>
                      )}
                    </div>
                  ))}

                <div className="fin-abgleich-knoepfe">
                  <button
                    type="button"
                    className="fin-uebernehmen"
                    onClick={() => saldenUebernehmen.mutate()}
                    disabled={
                      saldenUebernehmen.isPending || !salden.monate.some((m) => m.lesbar)
                    }
                  >
                    {saldenUebernehmen.isPending
                      ? 'Wird übernommen …'
                      : `${salden.monate.filter((m) => m.lesbar).length} Monate übernehmen`}
                  </button>
                  <button
                    type="button"
                    className="fin-abbrechen"
                    onClick={() => setSalden(null)}
                    disabled={saldenUebernehmen.isPending}
                  >
                    Schließen
                  </button>
                </div>
              </div>
            )}

            {monateOffen && (
              <div className="fin-monatspflege">
                <div className="fin-monatspflege-kopf">
                  <span className="fin-label">Monatszahlen {d.jahr} · ohne Umsatzsteuer</span>
                  <span className="fin-hinweis">
                    Aus dem Buchhaltungsbericht übertragen. Leere Monate bleiben im Diagramm außen
                    vor.
                  </span>
                </div>
                <div className="fin-monatsraster">
                  {d.monatsWerte.map((m) => (
                    <div className="fin-monatsfeld" key={m.monat}>
                      <span className="fin-monatsfeld-label">{m.label}</span>
                      <label>
                        <span>Umsatz</span>
                        <input
                          className="fin-monatsfeld-eingabe"
                          defaultValue={m.umsatz ? gruppiere(m.umsatz) : ''}
                          placeholder="0"
                          inputMode="numeric"
                          onBlur={(e) => {
                            const wert = betragLesen(e.target.value) ?? 0;
                            e.target.value = wert ? gruppiere(wert) : '';
                            if (wert !== m.umsatz) {
                              monatSpeichern.mutate({
                                monat: m.monat,
                                umsatz: wert,
                                ausgaben: m.ausgaben,
                              });
                            }
                          }}
                        />
                      </label>
                      <label>
                        <span>Ausgaben</span>
                        <input
                          className="fin-monatsfeld-eingabe"
                          defaultValue={m.ausgaben ? gruppiere(m.ausgaben) : ''}
                          placeholder="0"
                          inputMode="numeric"
                          onBlur={(e) => {
                            const wert = betragLesen(e.target.value) ?? 0;
                            e.target.value = wert ? gruppiere(wert) : '';
                            if (wert !== m.ausgaben) {
                              monatSpeichern.mutate({
                                monat: m.monat,
                                umsatz: m.umsatz,
                                ausgaben: wert,
                                stand: m.stand,
                                quelle: m.quelle,
                              });
                            }
                          }}
                        />
                      </label>
                      {/* Ohne Stand weiß später niemand mehr, ob eine Zahl
                          vorläufig oder endgültig ist. */}
                      <label>
                        <span>Stand</span>
                        <input
                          type="date"
                          className="fin-monatsfeld-eingabe"
                          defaultValue={m.stand}
                          max={meta.heute}
                          onBlur={(e) => {
                            if (e.target.value === m.stand) return;
                            monatSpeichern.mutate({
                              monat: m.monat,
                              umsatz: m.umsatz,
                              ausgaben: m.ausgaben,
                              stand: e.target.value,
                              quelle: m.quelle || `Buchhaltungsbericht ${m.label} ${d.jahr}`,
                            });
                          }}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {d.monate.length > 0 ? (
              <div className="fin-diagramm">
                {d.monate.map((m) => (
                  <div className="fin-monat" key={m.label}>
                    <div className="fin-balken">
                      <div
                        title={`Umsatz ${m.umsatzText}`}
                        className="fin-balken-teil"
                        style={{ height: m.hUmsatz, background: '#2A2320' }}
                      />
                      <div
                        title={`Ausgaben ${m.ausgabenText}`}
                        className="fin-balken-teil"
                        style={{ height: m.hAusgaben, background: '#CBBCAC' }}
                      />
                      <div
                        title={`Gewinn ${m.gewinnText}`}
                        className="fin-balken-teil"
                        style={{ height: m.hGewinn, background: 'var(--eh-rot)' }}
                      />
                    </div>
                    <span className="fin-monat-label">{m.label}</span>
                    <span className="fin-monat-gewinn">{m.gewinnKurz}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="fin-leer">
                Noch keine Monatszahlen erfasst. Sie kommen aus den Buchhaltungsberichten und werden
                bis auf Weiteres von Hand eingetragen.
              </div>
            )}
          </div>

          {/* Kumulierter Verlauf. Die Balken oben zeigen das Auf und Ab je
              Monat, diese Kurve zeigt die Richtung übers Jahr – zwei
              verschiedene Fragen, deshalb zwei Darstellungen. */}
          {d.verlauf.length > 1 && (
            <div className="fin-karte">
              <div className="fin-karte-kopf">
                <div className="fin-label">Entwicklung {d.zeitraum.label} · aufsummiert</div>
                <div className="fin-legende">
                  <span>
                    <span className="fin-legende-punkt" style={{ background: '#2A2320' }} />
                    Umsatz kumuliert
                  </span>
                  <span>
                    <span className="fin-legende-punkt" style={{ background: 'var(--eh-rot)' }} />
                    Gewinn kumuliert
                  </span>
                </div>
              </div>
              <Verlaufskurve verlauf={d.verlauf} max={d.verlaufMax} />
            </div>
          )}

          <div className="fin-zweispaltig">
            <div className="fin-karte fin-kumuliert">
              <div className="fin-label">Kumuliert · {d.zeitraum.label}</div>
              <div className="fin-kum-liste">
                {d.kumuliert.map((k) => (
                  <div className="fin-kum-zeile" key={k.label}>
                    <div className="fin-kum-kopf">
                      <span className="fin-kum-label">{k.label}</span>
                      <span className="fin-kum-wert">{k.wert}</span>
                    </div>
                    <div className="fin-kum-schiene">
                      <div
                        className="fin-kum-fuellung"
                        style={{ width: k.breite, background: k.farbe }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="fin-prognose">
              <div className="fin-prognose-label">Gewinnprognose</div>

              {/*
                Offene Aufträge gehören zur Gegenwart. Auf einen abgeschlossenen
                Zeitraum addiert ergäben sie eine Zahl, die es nie gegeben hat –
                deshalb sagt die Karte hier lieber, dass sie nichts sagt.
              */}
              {!d.prognose.gilt ? (
                <div className="fin-prognose-ruht">
                  Für einen abgeschlossenen Zeitraum gibt es keine Prognose. Die offenen Aufträge
                  von heute gehören nicht zu {d.zeitraum.label}.
                  <div className="fin-prognose-ist">
                    Ist-Gewinn laut Buchhaltung: <strong>{d.prognose.ist}</strong>
                  </div>
                </div>
              ) : (
                <>
              <div className="fin-prognose-summe">{d.prognose.summe}</div>
              <div className="fin-prognose-schiene">
                <div style={{ width: d.prognose.istBreite, background: 'var(--eh-rot)' }} />
                <div style={{ width: d.prognose.offenBreite, background: '#8A7D73' }} />
              </div>
              <div className="fin-prognose-zeilen">
                <div className="fin-prognose-zeile">
                  <span>Ist-Gewinn laut Buchhaltung</span>
                  <span className="fin-prognose-zahl">{d.prognose.ist}</span>
                </div>
                <div className="fin-prognose-zeile">
                  <span>erwarteter Beitrag offener Aufträge</span>
                  <span className="fin-prognose-zahl">{d.prognose.beitrag}</span>
                </div>
                <div className="fin-prognose-anzahl">
                  aus {d.prognose.offen} Auftragsvolumen · {d.prognose.anzahl}
                </div>

                {/* Aufschlüsselung: ohne sie ist die Prognose eine Zahl, die man
                    glauben muss. Mit ihr sieht man, woher sie kommt. */}
                {d.prognose.zeilen.length > 0 && (
                  <div className="fin-prognose-kategorien">
                    {d.prognose.zeilen.map((z) => (
                      <div className="fin-prognose-kat" key={z.prefix}>
                        <span className="fin-prognose-kat-name">
                          {z.prefix} · {z.auftragswert}
                        </span>
                        <span className="fin-prognose-kat-marge">
                          {z.ohneAufschlag ? 'kein Aufschlag hinterlegt' : `${z.aufschlag} % → ${z.margeText}`}
                        </span>
                        <span className="fin-prognose-kat-wert">{z.beitrag}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="fin-prognose-fuss">
                {d.prognose.ohneAufschlag.length > 0 ? (
                  <>
                    Für {d.prognose.ohneAufschlag.join(', ')} ist kein Kalkulationsaufschlag
                    hinterlegt – diese Aufträge zählen mit null. In den Einstellungen unter
                    „Kalkulationsaufschläge" nachtragen.
                  </>
                ) : (
                  <>
                    Ist-Gewinn plus Deckungsbeitrag der offenen Aufträge, gerechnet mit dem
                    hinterlegten Aufschlag je Kategorie. Der Auftragswert selbst zählt nicht als
                    Gewinn.
                  </>
                )}
              </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* --- Buchhaltungsberichte --- */}
      <div className="fin-karte">
        <div className="fin-karte-kopf">
          <div className="fin-label">Monatliche Buchhaltungsberichte</div>
          <div className="fin-kopf-rechts">
            <div className="fin-anzahl">{b?.anzahlText ?? ''}</div>
            {b?.buchhaltungAktiv && !abgleich && (
              <button
                type="button"
                className="fin-einlesen"
                onClick={() => pruefen.mutate()}
                disabled={pruefen.isPending}
                title="Sieht in SharePoint nach – schreibt noch nichts"
              >
                {pruefen.isPending ? 'Wird gesucht …' : 'Mit SharePoint abgleichen'}
              </button>
            )}
          </div>
        </div>

        {abgleich && (
          <div className="fin-abgleich">
            <div className="fin-abgleich-kopf">
              In SharePoint gefunden: <strong>{abgleich.gefunden}</strong> Berichte
              {abgleich.jahre.length ? ` (${abgleich.jahre.join(', ')})` : ''}
            </div>

            <ul className="fin-abgleich-liste">
              <li>
                <span className="fin-abgleich-zahl neu">{abgleich.neu.length}</span>
                werden neu übernommen
                {abgleich.neu.length > 0 && (
                  <span className="fin-abgleich-monate">
                    {abgleich.neu.map((m) => m.label).join(' · ')}
                  </span>
                )}
              </li>
              <li>
                <span className="fin-abgleich-zahl">{abgleich.verknuepfen.length}</span>
                bekommen nur die Verknüpfung nachgetragen
                {abgleich.verknuepfen.length > 0 && (
                  <span className="fin-abgleich-monate">
                    {abgleich.verknuepfen.map((m) => m.label).join(' · ')}
                  </span>
                )}
              </li>
              <li>
                <span className="fin-abgleich-zahl">{abgleich.unveraendert.length}</span>
                sind bereits vollständig – bleiben unverändert
              </li>
              {abgleich.luecken.length > 0 && (
                <li>
                  <span className="fin-abgleich-zahl fehlt">{abgleich.luecken.length}</span>
                  fehlen ganz – weder in SharePoint noch in KORA
                  <span className="fin-abgleich-monate">
                    {abgleich.luecken.map((m) => m.label).join(' · ')}
                  </span>
                </li>
              )}
            </ul>

            <div className="fin-abgleich-knoepfe">
              <button
                type="button"
                className="fin-uebernehmen"
                onClick={() => einlesen.mutate()}
                disabled={
                  einlesen.isPending ||
                  abgleich.neu.length + abgleich.verknuepfen.length === 0
                }
              >
                {einlesen.isPending
                  ? 'Wird übernommen …'
                  : abgleich.neu.length + abgleich.verknuepfen.length === 0
                    ? 'Nichts zu übernehmen'
                    : `${abgleich.neu.length + abgleich.verknuepfen.length} übernehmen`}
              </button>
              <button
                type="button"
                className="fin-abbrechen"
                onClick={() => setAbgleich(null)}
                disabled={einlesen.isPending}
              >
                Schließen
              </button>
            </div>
          </div>
        )}

        <div className="fin-upload-zeile">
          <label className="fin-feld">
            <span>Berichtsmonat</span>
            <select
              className="fin-auswahl"
              value={monat || b?.monate[0]?.wert || ''}
              onChange={(e) => setMonat(e.target.value)}
            >
              {(b?.monate ?? []).map((o) => (
                <option key={o.wert} value={o.wert}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label className="fin-dropzone">
            <span className="fin-dropzone-plus">+</span>
            <span className="fin-dropzone-text">
              <span className="fin-dropzone-titel">
                {hochladen.isPending ? 'Wird hochgeladen …' : 'PDF hochladen'}
              </span>
              {/* Zeigt vorab den vollständigen Zielpfad. „Automatisch" ist nur
                  dann angenehm, wenn man sieht, was automatisch passiert. */}
              <span className="fin-dropzone-hinweis">
                {zielPfad
                  ? `Wird abgelegt unter ${zielPfad}`
                  : 'Buchhaltungsbericht vom Steuerberater · ein PDF pro Monat'}
              </span>
            </span>
            <input
              ref={dateiFeld}
              type="file"
              accept="application/pdf"
              style={{ display: 'none' }}
              onChange={(e) => {
                const datei = e.target.files?.[0];
                setUploadFehler('');
                if (datei) hochladen.mutate(datei);
                e.target.value = '';
              }}
            />
          </label>
        </div>

        {uploadFehler && <div className="eh-fehler">{uploadFehler}</div>}

        <div className="fin-tabelle">
          <div className="fin-tabelle-zeile fin-tabelle-kopf">
            <div>Monat</div>
            <div>Hochgeladen</div>
            <div>Datei</div>
            <div>Notiz</div>
            <div style={{ textAlign: 'right' }}>Datei</div>
          </div>

          {(b?.berichte ?? []).map((bericht) => (
            <div className="fin-tabelle-zeile" key={bericht.id}>
              <div className="fin-bericht-monat">{bericht.monatLabel}</div>
              <div className="fin-bericht-datum">{bericht.hochText}</div>
              <div className="fin-bericht-datei">{bericht.datei}</div>
              <input
                className="fin-bericht-notiz"
                defaultValue={bericht.notiz}
                placeholder="Kernaussage, Auffälligkeit …"
                onBlur={(e) => {
                  if (e.target.value !== bericht.notiz) {
                    notiz.mutate({ id: bericht.id, text: e.target.value });
                  }
                }}
              />
              <div style={{ textAlign: 'right' }}>
                {bericht.hatUrl ? (
                  <a href={bericht.downloadUrl} target="_blank" rel="noreferrer">
                    Öffnen ↗
                  </a>
                ) : (
                  <span className="fin-bericht-ohne" title="Datei liegt nicht in SharePoint">
                    nur Eintrag
                  </span>
                )}
              </div>
            </div>
          ))}

          {b && b.berichte.length === 0 && (
            <div className="fin-leer">Noch kein Bericht abgelegt.</div>
          )}
        </div>

        <div className="fin-hinweis">
          Die Berichte werden abgelegt, aber bewusst nicht inhaltlich ausgewertet. Die Monatszahlen
          im Diagramm werden getrennt gepflegt.
        </div>
      </div>
    </div>
  );
}

/**
 * Kumulierter Jahresverlauf als Kurve.
 *
 * Bewusst als SVG von Hand statt mit einer Diagramm-Bibliothek: es sind zwei
 * Linien auf einer gemeinsamen Skala, und eine Abhängigkeit von 200 kB
 * Fremdcode wäre dafür unverhältnismäßig. Die Skala richtet sich am kumulierten
 * Umsatz aus; der Gewinn liegt darunter und wird dadurch als Anteil lesbar.
 */
function Verlaufskurve({
  verlauf,
  max,
}: {
  verlauf: FinanzenDaten['verlauf'];
  max: number;
}) {
  const B = 800;
  const H = 220;
  const rand = { oben: 14, unten: 30, links: 8, rechts: 8 };
  const zeichenH = H - rand.oben - rand.unten;
  const zeichenB = B - rand.links - rand.rechts;

  // Auch negative Gewinne müssen Platz haben – sonst verschwindet die Kurve.
  const tief = Math.min(0, ...verlauf.map((v) => v.gewinn));
  const spanne = Math.max(1, max - tief);

  const x = (i: number) =>
    rand.links + (verlauf.length === 1 ? zeichenB / 2 : (i / (verlauf.length - 1)) * zeichenB);
  const y = (wert: number) => rand.oben + zeichenH - ((wert - tief) / spanne) * zeichenH;

  const pfad = (feld: 'umsatz' | 'gewinn') =>
    verlauf.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(v[feld]).toFixed(1)}`).join(' ');

  const nullLinie = tief < 0 ? y(0) : null;

  return (
    <div className="fin-kurve">
      <svg viewBox={`0 0 ${B} ${H}`} preserveAspectRatio="none" role="img"
        aria-label={`Kumulierter Umsatz und Gewinn, ${verlauf[0]?.label} bis ${verlauf[verlauf.length - 1]?.label}`}>
        {nullLinie !== null && (
          <line x1={rand.links} x2={B - rand.rechts} y1={nullLinie} y2={nullLinie}
            stroke="var(--eh-rahmen)" strokeWidth="1" strokeDasharray="4 4" />
        )}
        <path d={pfad('umsatz')} fill="none" stroke="#2A2320" strokeWidth="2.5"
          strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <path d={pfad('gewinn')} fill="none" stroke="var(--eh-rot)" strokeWidth="2.5"
          strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {verlauf.map((v, i) => (
          <g key={v.monat}>
            <circle cx={x(i)} cy={y(v.umsatz)} r="3.5" fill="#2A2320" />
            <circle cx={x(i)} cy={y(v.gewinn)} r="3.5" fill="var(--eh-rot)" />
            <title>{`${v.label}: Umsatz ${v.umsatzText} · Gewinn ${v.gewinnText}`}</title>
          </g>
        ))}
      </svg>
      <div className="fin-kurve-achse">
        {verlauf.map((v) => (
          <span key={v.monat}>{v.label}</span>
        ))}
      </div>
      <div className="fin-kurve-fuss">
        Stand {verlauf[verlauf.length - 1]?.label}: Umsatz{' '}
        <strong>{verlauf[verlauf.length - 1]?.umsatzText}</strong>, Gewinn{' '}
        <strong>{verlauf[verlauf.length - 1]?.gewinnText}</strong> – jeweils seit Jahresbeginn.
      </div>
    </div>
  );
}

/**
 * Auswertungszeitraum.
 *
 * Steht bewusst ganz oben und nicht in einer Einstellung: Jede Zahl auf dieser
 * Seite hängt daran, also muss man sehen können, worüber gerade gerechnet wird.
 *
 * Zur Auswahl stehen nur Monate, für die ein Bericht erfasst ist. Ein Jahr, das
 * erst im Juli beginnt, heißt hier auch „2025 · Jul–Dez" – sonst sieht ein
 * halbes Jahr fehlender Daten aus wie ein Einbruch.
 */
function Zeitraumwahl({
  daten,
  laeuft,
  onWaehlen,
}: {
  daten: FinanzenDaten;
  laeuft: boolean;
  onWaehlen: (von: string, bis: string) => void;
}) {
  const { zeitraum, verfuegbar } = daten;
  const alle = verfuegbar.monate;

  if (alle.length === 0) {
    return (
      <div className="fin-zeitraum fin-zeitraum--leer">
        Noch keine Buchhaltungsberichte erfasst – sobald welche eingelesen sind, lässt sich hier der
        Auswertungszeitraum wählen.
      </div>
    );
  }

  const vorschlaege: { schluessel: string; label: string; von: string; bis: string }[] = [];
  for (const j of verfuegbar.jahre) {
    vorschlaege.push({ schluessel: j.jahr, label: j.label, von: j.von, bis: j.bis });
  }
  if (alle.length > 12) {
    const start = alle[alle.length - 12]!.wert;
    vorschlaege.push({
      schluessel: '12m',
      label: 'Letzte 12 Monate',
      von: start,
      bis: verfuegbar.letzter,
    });
  }
  if (verfuegbar.jahre.length > 1) {
    vorschlaege.push({
      schluessel: 'alle',
      label: `Alle Berichte · ${alle.length}`,
      von: verfuegbar.erster,
      bis: verfuegbar.letzter,
    });
  }
  vorschlaege.push({
    schluessel: 'letzter',
    label: 'Letzter Monat',
    von: verfuegbar.letzter,
    bis: verfuegbar.letzter,
  });

  return (
    <div className="fin-zeitraum">
      <div className="fin-zeitraum-kopf">
        <div>
          <div className="fin-zeitraum-label">Auswertungszeitraum</div>
          <div className="fin-zeitraum-wert">
            {zeitraum.label}
            {laeuft && <span className="fin-zeitraum-laeuft">wird berechnet …</span>}
          </div>
        </div>

        {/*
          Die Anzahl der Berichte gehört neben den Zeitraum. „Juli bis Juni"
          klingt nach zwölf Monaten – ob es zwölf Berichte sind, steht hier.
        */}
        <div className="fin-zeitraum-bilanz">
          {zeitraum.mitBericht === zeitraum.monate ? (
            <span className="fin-zeitraum-voll">
              {zeitraum.monate === 1 ? '1 Bericht' : `${zeitraum.monate} Berichte`} · lückenlos
            </span>
          ) : (
            <span className="fin-zeitraum-luecke">
              {zeitraum.mitBericht} von {zeitraum.monate} Monaten erfasst
              {zeitraum.luecken.length > 0 && (
                <span className="fin-zeitraum-fehlt">
                  Es fehlt: {zeitraum.luecken.join(', ')}
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      <div className="fin-zeitraum-wahl">
        <div className="fin-zeitraum-schnell">
          {vorschlaege.map((v) => {
            const aktiv = zeitraum.von === v.von && zeitraum.bis === v.bis;
            return (
              <button
                key={v.schluessel}
                type="button"
                className={`fin-zeitraum-knopf ${aktiv ? 'aktiv' : ''}`}
                onClick={() => onWaehlen(v.von, v.bis)}
              >
                {v.label}
              </button>
            );
          })}
        </div>

        <div className="fin-zeitraum-genau">
          <label>
            <span>von</span>
            <select
              value={zeitraum.von}
              onChange={(e) => onWaehlen(e.target.value, zeitraum.bis)}
            >
              {alle.map((m) => (
                <option key={m.wert} value={m.wert}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>bis</span>
            <select
              value={zeitraum.bis}
              onChange={(e) => onWaehlen(zeitraum.von, e.target.value)}
            >
              {alle.map((m) => (
                <option key={m.wert} value={m.wert}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
    </div>
  );
}
